import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { AppLayout } from './components/AppLayout';
import { LoadingState } from './components/LoadingState';
import { NotFoundPage } from './components/NotFoundPage';
import { publicConfig } from './config/env';

const ScopedLinkPage = lazy(() =>
  import('./features/links/ScopedLinkPage').then((module) => ({
    default: module.ScopedLinkPage,
  })),
);
const AttendanceResponsePage = lazy(() =>
  import('./features/attendance/AttendanceResponsePage').then((module) => ({
    default: module.AttendanceResponsePage,
  })),
);
const ParentConnectionPage = lazy(() =>
  import('./features/parents/ParentConnectionPage').then((module) => ({
    default: module.ParentConnectionPage,
  })),
);

const OrderPage = lazy(() =>
  import('./features/payments/OrderPage').then((module) => ({ default: module.OrderPage })),
);
const InvitationPaymentPage = lazy(() =>
  import('./features/payments/InvitationPaymentPage').then((module) => ({
    default: module.InvitationPaymentPage,
  })),
);
const TrainerProfilePage = lazy(() =>
  import('./features/trainers/TrainerProfilePage').then((module) => ({
    default: module.TrainerProfilePage,
  })),
);
const TrainerCheckoutPage = lazy(() =>
  import('./features/carts/CheckoutPage').then((module) => ({
    default: module.TrainerCheckoutPage,
  })),
);
const OrganizationBookingPage = lazy(() =>
  import('./features/trainers/BookingPage').then((module) => ({
    default: module.OrganizationBookingPage,
  })),
);
const OrganizationCheckoutPage = lazy(() =>
  import('./features/carts/CheckoutPage').then((module) => ({
    default: module.OrganizationCheckoutPage,
  })),
);
const SetBookingPage = lazy(() =>
  import('./features/trainers/BookingPage').then((module) => ({
    default: module.SetBookingPage,
  })),
);
const SetCheckoutPage = lazy(() =>
  import('./features/carts/CheckoutPage').then((module) => ({
    default: module.SetCheckoutPage,
  })),
);
const CartOrderPage = lazy(() =>
  import('./features/carts/CartOrderPage').then((module) => ({ default: module.CartOrderPage })),
);
const ProviderArea = lazy(() =>
  import('./features/provider/ProviderArea').then((module) => ({ default: module.ProviderArea })),
);
const CoachArea = lazy(() =>
  import('./features/coach/CoachArea').then((module) => ({ default: module.CoachArea })),
);
const RootGate = lazy(() =>
  import('./features/account/RootGate').then((module) => ({ default: module.RootGate })),
);
const AccountHome = lazy(() =>
  import('./features/account/AccountHome').then((module) => ({ default: module.AccountHome })),
);
const HomeEntry = lazy(() =>
  import('./features/athlete/AthleteArea').then((module) => ({ default: module.HomeEntry })),
);
const SignUpPage = lazy(() =>
  import('./features/athlete/AccountPages').then((module) => ({ default: module.SignUpPage })),
);
const VerifyPage = lazy(() =>
  import('./features/athlete/AccountPages').then((module) => ({ default: module.VerifyPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./features/athlete/AccountPages').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const JoinPage = lazy(() =>
  import('./features/athlete/JoinPage').then((module) => ({ default: module.JoinPage })),
);
const ProviderLoginPage = lazy(() =>
  import('./features/provider/ProviderArea').then((module) => ({
    default: module.ProviderLoginPage,
  })),
);

const AccountPage = lazy(() =>
  import('./features/account/AccountPages').then((module) => ({ default: module.AccountPage })),
);
const SocialCallbackPage = lazy(() =>
  import('./features/account/AccountPages').then((module) => ({
    default: module.SocialCallbackPage,
  })),
);

function LazyRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

/**
 * Build the route table.
 *
 * `coachWorkspace` and `athleteWeb` are parameters rather than direct reads so every state is
 * reachable from a test; they default to what the deployment configured.
 */
export function createAppRouter({
  coachWorkspace = publicConfig.coachWorkspaceEnabled,
  athleteWeb = publicConfig.athleteWebEnabled,
}: { coachWorkspace?: boolean; athleteWeb?: boolean } = {}) {
  return createBrowserRouter([
    {
      path: '/',
      element: <AppLayout />,
      children: [
        {
          index: true,
          element:
            coachWorkspace || athleteWeb ? (
              <LazyRoute>
                <RootGate />
              </LazyRoute>
            ) : (
              /*
               * Without the workspace there is nothing for `/` to decide between: bookings and
               * payments is the signed-in app. An unauthenticated visitor is bounced from there to
               * sign-in carrying `/manage` as the destination, so sign-in still lands where it did.
               */
              <Navigate to="/manage" replace />
            ),
        },
        {
          path: 'auth/callback',
          element: (
            <LazyRoute>
              <SocialCallbackPage />
            </LazyRoute>
          ),
        },
        {
          path: 'account',
          element: (
            <LazyRoute>
              <AccountPage />
            </LazyRoute>
          ),
        },
        {
          path: 'account/setup',
          element: (
            <LazyRoute>
              <AccountPage setup />
            </LazyRoute>
          ),
        },
        ...(['setup', 'confirm', 'preferences'] as const).map((mode) => ({
          path: `parent-connections/${mode}`,
          element: (
            <LazyRoute>
              <ParentConnectionPage mode={mode} />
            </LazyRoute>
          ),
        })),
        {
          path: 'login',
          element: (
            <LazyRoute>
              <ProviderLoginPage />
            </LazyRoute>
          ),
        },
        {
          path: 'manage/*',
          element: (
            <LazyRoute>
              <ProviderArea />
            </LazyRoute>
          ),
        },
        // The athlete web (PLA-1655): public account pages, the join page, and `/home` with the
        // athlete area for athletes. Absent where it is off, like the workspace below.
        ...(athleteWeb
          ? [
              ...(
                [
                  ['signup', <SignUpPage key="signup" />],
                  ['signup/verify', <VerifyPage key="verify" />],
                  ['forgot-password', <ForgotPasswordPage key="forgot" />],
                  ['join', <JoinPage key="join" />],
                ] as const
              ).map(([path, page]) => ({ path, element: <LazyRoute>{page}</LazyRoute> })),
              {
                path: 'home/*',
                element: (
                  <LazyRoute>
                    <HomeEntry />
                  </LazyRoute>
                ),
              },
            ]
          : coachWorkspace
            ? [
                {
                  path: 'home',
                  element: (
                    <LazyRoute>
                      <AccountHome />
                    </LazyRoute>
                  ),
                },
              ]
            : []),
        // The coach workspace. Where it is off it is absent rather than empty, so an old link
        // answers "not found" instead of a shell with nothing behind it.
        ...(coachWorkspace
          ? [
              {
                path: 'coach/*',
                element: (
                  <LazyRoute>
                    <CoachArea />
                  </LazyRoute>
                ),
              },
            ]
          : []),
        {
          path: 'trainers/:trainerId',
          element: (
            <LazyRoute>
              <TrainerProfilePage />
            </LazyRoute>
          ),
        },
        {
          path: 'trainers/:trainerId/checkout',
          element: (
            <LazyRoute>
              <TrainerCheckoutPage />
            </LazyRoute>
          ),
        },
        {
          path: 'organizations/:organizationId/book',
          element: (
            <LazyRoute>
              <OrganizationBookingPage />
            </LazyRoute>
          ),
        },
        {
          path: 'organizations/:organizationId/book/checkout',
          element: (
            <LazyRoute>
              <OrganizationCheckoutPage />
            </LazyRoute>
          ),
        },
        {
          path: 'sets/:token',
          element: (
            <LazyRoute>
              <SetBookingPage />
            </LazyRoute>
          ),
        },
        {
          path: 'sets/:token/checkout',
          element: (
            <LazyRoute>
              <SetCheckoutPage />
            </LazyRoute>
          ),
        },
        {
          path: 'bookings/carts/:cartId',
          element: (
            <LazyRoute>
              <CartOrderPage />
            </LazyRoute>
          ),
        },
        {
          path: 'bookings/invitations/:eventId',
          element: (
            <LazyRoute>
              <InvitationPaymentPage />
            </LazyRoute>
          ),
        },
        {
          path: 'bookings/orders/:orderId',
          element: (
            <LazyRoute>
              <OrderPage />
            </LazyRoute>
          ),
        },
        {
          path: 'links/:token',
          element: (
            <LazyRoute>
              <ScopedLinkPage />
            </LazyRoute>
          ),
        },
        {
          path: 'attendance-response',
          element: (
            <LazyRoute>
              <AttendanceResponsePage />
            </LazyRoute>
          ),
        },
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
  ]);
}

export const router = createAppRouter();
